/**
 * The API boundary's shapes: where a value came from, and what a broken contract carries.
 * See docs/rules/api_boundary.md.
 */

import type { z } from 'zod';

import type { ExternalStore, Unsubscribe } from './common';

export enum ViolationSourceKind {
    Cloud = 'cloud',
    Relay = 'relay',
    /** Another browser, such as a viewer of the same video wall. */
    Peer = 'peer',
}

/** Which side sent a value; the reporter grades a violation by it. */
export type ViolationSource =
    { kind: ViolationSourceKind.Cloud } |
    { kind: ViolationSourceKind.Relay; serverId: string } |
    { kind: ViolationSourceKind.Peer };

export enum ViolationKind {
    /** The value broke its schema. */
    Mismatch = 'mismatch',
    /** A frame named a type its union does not declare. */
    UnknownFrame = 'unknownFrame',
}

export enum ViolationSeverity {
    /** A bug: both sides are current and still disagree. */
    Major = 'major',
    /** Expected skew, or a frame the UI can do without. */
    Minor = 'minor',
}

/** Where a value came from, as a violation names it. */
export interface ViolationOrigin {
    /** The URL path without its query. */
    endpoint: string;
    source: ViolationSource;
}

/** An HTTP answer's origin; the status rides on the ContractError it may become. */
export interface AnswerOrigin extends ViolationOrigin {
    status: number;
}

/** One step from an answer's root: a field or record key, a row's own key, or a row's index. */
export type PathSegment = string | number;

/** A row's key, read from the raw row; null where it has none to read. */
export type RowKeyReader = (row: unknown) => string | null;

/** One kind of frame in a stream with no type field, told apart by a key only it carries. */
export interface KeyedOption<S extends z.ZodType = z.ZodType> {
    key: string;
    schema: S;
}

/** A frame of a keyed stream, with the key that told its kind, so a reader switches on it. */
export interface KeyedFrame<K extends string, F> {
    key: K;
    frame: F;
}

interface ViolationBase extends ViolationOrigin {
    issues: z.core.$ZodIssue[];
    atMs: number;
}

/** A value that broke its contract. */
export type ContractViolation =
    (ViolationBase & {
        kind: ViolationKind.Mismatch;
        /** Where a dropped row sat in the answer; absent when the value failed whole. */
        path?: PathSegment[];
    }) |
    (ViolationBase & { kind: ViolationKind.UnknownFrame; frameType: string });

/** A row a `listOf` or `recordOf` schema dropped, by its path below the level reporting it. */
export interface DroppedRow {
    path: PathSegment[];
    issues: z.core.$ZodIssue[];
}

/** A violation the reporter has graded. */
export type GradedViolation = ContractViolation & { severity: ViolationSeverity };

export interface ViolationSink {
    /** Never rejects. */
    report(violation: ContractViolation): Promise<void>;
}

/** Answers whether a media_server runs the newest release. */
export type ServerGate = (serverId: string) => boolean;

/** The one sink: grades each violation and shows or files it; the snapshot is the raised list. */
export interface ViolationReporter
    extends ViolationSink, ExternalStore<readonly GradedViolation[]> {
    /** Clears what was raised on screen. */
    dismiss(): void;
    /** Hands `listener` each minor violation as it is raised. */
    subscribeMinor(listener: (violation: GradedViolation) => void): Unsubscribe;
    setServerGate(gate: ServerGate): void;
}

/** Whether a newer build is served than the one this page runs; the snapshot is that answer. */
export interface VersionWatch extends ExternalStore<boolean> {
    /** Reads the served version if the last read is stale. True when outdated; never rejects. */
    check(): Promise<boolean>;
}

/** How `fetchRows` asks: the request's options, and how to name a row in a report. */
export interface RowsRequest {
    init?: RequestInit;
    getKey?: RowKeyReader;
}

/** The parsed HTTP helpers, bound to the source of the answers they read. */
export interface Boundary {
    fetchParsed<S extends z.ZodType>(
        url: string, schema: S, init?: RequestInit,
    ): Promise<z.output<S>>;
    fetchRows<S extends z.ZodType>(
        url: string, itemSchema: S, options?: RowsRequest,
    ): Promise<z.output<S>[]>;
    postParsed<S extends z.ZodType>(
        url: string, body: unknown, schema: S, init?: RequestInit,
    ): Promise<z.output<S>>;
    putParsed<S extends z.ZodType>(url: string, body: unknown, schema: S): Promise<z.output<S>>;
    patchParsed<S extends z.ZodType>(url: string, body: unknown, schema: S): Promise<z.output<S>>;
    deleteParsed<S extends z.ZodType>(url: string, schema: S): Promise<z.output<S>>;
    postFormParsed<S extends z.ZodType>(
        url: string, form: FormData, schema: S,
    ): Promise<z.output<S>>;
}
